import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router";
import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  Clock,
} from "lucide-react";
import Images from "../../utils/Images";

/** X (Twitter) brand mark; Lucide still ships the legacy bird icon */
const XLogo = ({ size = 20, className = "", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
    {...props}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const categoryLinks = [
    { name: "Real Estate & Mortgages", href: "/real-estate" },
    { name: "Legal", href: "/legal" },
    { name: "Health", href: "/health" },
    { name: "Careers", href: "/careers" },
    { name: "Nutrition & Meal Plans", href: "/nutrition" },
    { name: "Betting & Gaming", href: "/betting-gaming" },
  ];

  const companyLinks = [
    { name: "About InfoBoxx", href: "/about" },
    { name: "Categories", href: "/#categories" },
    { name: "Become a Partner", href: "/partners#how-to-become-a-partner" },
    { name: "Contact", href: "/contact" },
    { name: "FAQs", href: "/faqs" },
  ];

  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms & Conditions", href: "/terms" },
    { name: "Cookie Policy", href: "/cookies" },
  ];

  const strategyPillars = [
    { label: "Capture Intent", sub: "User lands with a question", emoji: "🎯" },
    { label: "Build Trust", sub: "Clear, structured explanation", emoji: "🛡️" },
    { label: "Drive Conversion", sub: "Ask · Learn · Talk · Book", emoji: "⚡" },
    { label: "Monetize", sub: "Partners · Leads · Engagement", emoji: "💰" },
  ];

  const socialLinks = [
    { name: "Facebook", icon: Facebook, href: "#", color: "#1877f2" },
    { name: "Instagram", icon: Instagram, href: "#", color: "#e4405f" },
    { name: "X (formerly Twitter)", icon: XLogo, href: "#", color: "#000000" },
    { name: "LinkedIn", icon: Linkedin, href: "#", color: "#0a66c2" },
  ];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <footer className="relative overflow-hidden text-black [font-family:'Onest',sans-serif]">
      {/* Warm editorial background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[#f7f4ec]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#f7f4ec] to-[#efe9dc] opacity-95"></div>
        <div className="absolute top-0 -left-40 w-80 h-80 bg-[#FFD221]/25 rounded-full filter blur-3xl opacity-60 animate-pulse"></div>
        <div className="absolute top-0 -right-40 w-80 h-80 bg-white rounded-full opacity-80 filter blur-3xl delay-1000 animate-pulse"></div>
        <div className="absolute -bottom-40 left-1/2 w-80 h-80 bg-[#FFD221]/20 rounded-full filter blur-3xl opacity-50 animate-pulse delay-500"></div>
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
          }}
        ></div>
      </div>

      <style>{`
        .premium-link {
          color: rgba(0,0,0,0.68);
          text-decoration: none;
          font-size: 14px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
        }
        .premium-link:hover {
          color: #000000;
          gap: 10px;
          transform: translateX(4px);
        }
        .premium-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 1px;
          background: linear-gradient(90deg, #FFD221, transparent);
          transition: width 0.3s ease;
        }
        .premium-link:hover::after {
          width: 100%;
        }

        .premium-heading {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          background: linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          margin-bottom: 24px;
          display: inline-block;
        }

        .stat-card {
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 16px;
          padding: 12px;
          text-align: center;
          transition: all 0.3s;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          border-color: rgba(0,0,0,0.18);
          background: #ffffff;
        }

        .contact-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 8px 0;
          transition: all 0.3s;
          border-radius: 8px;
        }
        .contact-item:hover {
          background: rgba(0,0,0,0.03);
          padding-left: 8px;
        }

        .social-icon-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.75rem;
          height: 2.75rem;
          border-radius: 0.75rem;
          border: 1px solid rgba(0,0,0,0.08);
          background: #ffffff;
          text-decoration: none;
          color: rgba(0,0,0,0.85);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .social-icon-btn:hover {
          border-color: #111111;
          background: #FFD221;
          transform: translateY(-2px);
        }
      `}</style>

      <div className="relative z-10 px-6 pt-16 pb-8 mx-auto max-w-8xl lg:px-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >

          {/* Main Grid */}
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-12 lg:gap-14">

            {/* Brand col */}
            <motion.div variants={itemVariants} className="lg:col-span-4">
              <Link
                to="/"
                className="inline-flex px-4 py-2 mb-5 bg-white rounded-2xl shadow-lg shadow-black/10"
              >
                <img src={Images.infoboxx_logo} alt="InfoBoxx" className="object-contain w-auto h-10" />
              </Link>

              <p className="mb-3 text-sm font-semibold tracking-wide text-[#c9a000]">
                Answers first. Qualified action next.
              </p>
              <p className="mb-7 text-sm leading-relaxed text-gray-600">
                InfoBoxx turns real questions into trusted content journeys, subscriber data, and
                qualified leads for verified service providers across Ghana.
              </p>

              <div className="space-y-3">
                <div className="contact-item">
                  <MapPin size={17} className="mt-0.5 flex-shrink-0 text-[#c9a000]" />
                  <span className="text-sm text-gray-600">
                    1st Floor GWOSEVO Building, 71 Swaniker Lane, Abelemkpe, Accra
                  </span>
                </div>
                <div className="contact-item">
                  <Phone size={17} className="flex-shrink-0 text-[#c9a000]" />
                  <a href="tel:+233200000000" className="text-sm text-gray-600 transition hover:text-black">
                    +233 20 000 0000
                  </a>
                </div>
                <div className="contact-item">
                  <Mail size={17} className="flex-shrink-0 text-[#c9a000]" />
                  <a href="mailto:info@infoboxx.com" className="text-sm text-gray-600 transition hover:text-black">
                    info@infoboxx.com
                  </a>
                </div>
                <div className="contact-item">
                  <Clock size={17} className="flex-shrink-0 text-[#c9a000]" />
                  <span className="text-sm text-gray-600">Mon–Fri: 8AM–8PM · Sat: 9AM–4PM</span>
                </div>
              </div>
            </motion.div>

            {/* Categories */}
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <h3 className="premium-heading">Categories</h3>
              <ul className="space-y-3">
                {categoryLinks.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className="premium-link">
                      {link.name}
                      <ChevronRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Company */}
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <h3 className="premium-heading">Company</h3>
              <ul className="space-y-3">
                {companyLinks.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className="premium-link">
                      {link.name}
                      <ChevronRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <h3 className="premium-heading">Legal</h3>
                <ul className="space-y-3">
                  {legalLinks.map((link) => (
                    <li key={link.name}>
                      <Link to={link.href} className="premium-link">
                        {link.name}
                        <ChevronRight size={14} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Social + Strategy note */}
            <motion.div variants={itemVariants} className="lg:col-span-4">
              <h3 className="premium-heading">Stay Connected</h3>
              <ul className="flex flex-wrap gap-3 p-0 m-0 mb-8 list-none">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  const isXLogo = Icon === XLogo;
                  return (
                    <li key={social.name}>
                      <motion.a
                        href={social.href}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label={social.name}
                        className="social-icon-btn"
                      >
                        {isXLogo ? (
                          <XLogo size={20} style={{ color: social.color }} />
                        ) : (
                          <Icon size={20} color={social.color} strokeWidth={1.75} />
                        )}
                      </motion.a>
                    </li>
                  );
                })}
              </ul>

              {/* Strategic pillars */}
              <div className="rounded-2xl border border-black/[0.07] bg-white p-4 shadow-[0_8px_28px_-16px_rgba(0,0,0,0.2)]">
                <p className="mb-3 text-[10px] font-black uppercase tracking-[0.2em] text-[#c9a000]">
                  🔥 Platform strategy
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {strategyPillars.map(({ label, sub, emoji }) => (
                    <div
                      key={label}
                      className="flex flex-col gap-1 rounded-xl border border-black/[0.06] bg-[#f7f4ec] p-3"
                    >
                      <span className="text-base leading-none" aria-hidden>{emoji}</span>
                      <p className="mt-1 text-xs font-black text-gray-900">{label}</p>
                      <p className="text-[10px] leading-snug text-gray-500">{sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Divider */}
          <motion.div variants={itemVariants} className="my-10">
            <div className="h-px bg-gradient-to-r from-transparent to-transparent via-black/15" />
          </motion.div>

          {/* Bottom Bar */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col gap-4 justify-between items-center md:flex-row"
          >
            <p className="text-sm text-gray-500">
              © {currentYear}{" "}
              <span className="font-semibold text-black">InfoBoxx</span>. All rights reserved.
            </p>

            <div className="flex flex-wrap gap-5 justify-center">
              {companyLinks.slice(0, 4).map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-xs text-gray-500 transition hover:text-black"
                >
                  {link.name}
                </Link>
              ))}
              <span className="text-xs text-gray-300">·</span>
              {legalLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-xs text-gray-500 transition hover:text-black"
                >
                  {link.name}
                </Link>
              ))}
            </div>

          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;